import type { User, UserInfo } from 'firebase/auth';

// Some legacy Firebase accounts have Google linked but no account-level email:
// `user.email` is null, and the address lives only on the linked Google
// sign-in (`providerData`), which Firebase takes from Google and refreshes on
// every Google sign-in. These helpers read the account's email the same way
// everywhere, falling back to that Google sign-in email.

const GOOGLE_PROVIDER_ID = 'google.com';

type EmailSource = Pick<User, 'email'> & {
  providerData?: ReadonlyArray<Pick<UserInfo, 'email' | 'providerId'>>;
};

function normalize(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

/** The email on the account's linked Google sign-in, or '' if it has none. */
export function getGoogleProviderEmail(user: EmailSource): string {
  return normalize(
    user.providerData?.find((info) => info.providerId === GOOGLE_PROVIDER_ID)?.email,
  );
}

/** The account's email for display: its own, else its Google sign-in's. */
export function getAccountEmail(user: EmailSource | null | undefined): string {
  if (!user) {
    return '';
  }

  return normalize(user.email) || getGoogleProviderEmail(user);
}

/**
 * The email a Google sign-in proved, to compare with the email the visitor
 * typed. The account's own email decides whenever it has one. Only an account
 * that existed before this sign-in may fall back to its Google sign-in's email:
 * Kudu no longer creates accounts through Google, so a just-created account
 * gets '' and is refused as before.
 */
export function getGoogleSignInEmail(
  user: EmailSource,
  { isNewUser }: { isNewUser: boolean },
): string {
  const ownEmail = normalize(user.email);

  if (ownEmail) {
    return ownEmail;
  }

  return isNewUser ? '' : getGoogleProviderEmail(user);
}
